import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { ChevronDown, Loader2, Mail, Save } from "lucide-react";
import { getDocData, setDocData } from "../../Firebase/Firebase.utils";
import { FormSection, TextField } from "./FormShell";
import { revalidatePublic } from "../utils/revalidatePublic";

// Dedicated card for the site-wide "apply email" default. Lives on the
// same pageSettings/careers doc that PublicPageSettings writes to, so
// heading + hero + SEO + apply email all end up on one doc.
//
// Kept as its own component (not merged into PublicPageSettings) so the
// generic shared component stays generic — this is a careers-only concern.

const SETTINGS_COLLECTION = "pageSettings";
const SETTINGS_KEY = "careers";
const DEFAULT_APPLY_EMAIL = "careers@leechiu.com";

export default function CareersGlobalSettings() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState("");
  const [initial, setInitial] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      const data = await getDocData(SETTINGS_COLLECTION, SETTINGS_KEY);
      if (!active) return;
      const seed = data?.applyEmail ?? "";
      setValue(seed);
      setInitial(seed);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const isDirty = value.trim() !== initial;

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDocData(SETTINGS_COLLECTION, SETTINGS_KEY, {
        applyEmail: value.trim(),
      });
      revalidatePublic(["/careers"]);
      setInitial(value.trim());
      toast.success("Careers settings saved.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-4 bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-leechiuBlue" />
          <div>
            <h3 className="text-sm font-semibold text-leechiuBlue m-0">
              Global apply email
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Fallback for jobs that don't override it. Currently:{" "}
              <span className="font-medium text-leechiuBlue">
                {loading
                  ? "…"
                  : initial || `${DEFAULT_APPLY_EMAIL} (default)`}
              </span>
            </p>
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="border-t border-gray-100 p-5">
          <FormSection>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            ) : (
              <>
                <TextField
                  label="Apply email"
                  type="email"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={DEFAULT_APPLY_EMAIL}
                  hint="Applied to every job that doesn't set its own apply email. Used in the 'Apply Now' mailto link and structured data."
                />
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!isDirty || saving}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-leechiuOrange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Save
                  </button>
                </div>
              </>
            )}
          </FormSection>
        </div>
      ) : null}
    </section>
  );
}
